import { ContentCard, Row, Button, formatNumber } from "@narmi/design_system";
import styles from "./Cards.module.css";
import React from "react";

const numberFormatter = new Intl.NumberFormat("en-US");

export function Summary({
  title,
  value,
  change,
  changeReferenceDate,
  showPercentChange = true,
}: {
  title: string;
  value: string;
  change: number;
  changeReferenceDate?: string;
  showPercentChange?: boolean;
}) {
  return (
    <ContentCard kind="bordered">
      <div className={styles.SummaryTitle}>{title}</div>
      <div
        className={`${styles.SummaryValue} ${changeReferenceDate ? "margin--y--s" : "margin--top--s"}`}
      >
        {value}
      </div>
      {changeReferenceDate ? (
        <div className={styles.SummaryChange}>
          {showPercentChange ? (
            change > 0 ? (
              <span className="fontColor--success">
                <i className="narmi-icon-trending-up" />
                {formatNumber(change, "percent")}{" "}
              </span>
            ) : (
              <span className="fontColor--error">
                <i className="narmi-icon-trending-down" />
                {formatNumber(change, "percent")}{" "}
              </span>
            )
          ) : null}
          since {changeReferenceDate}
        </div>
      ) : null}
    </ContentCard>
  );
}

export function SummaryRow({ children }: { children: React.ReactNode }) {
  return <div className={styles.SummaryRow}>{children}</div>;
}

/**
 * Returns the last bar value for the pipeline.
 *
 * The logic in this function was determined experimentally but the general idea is that we
 * wish to pick a value that's close to the maxCount but also divides nicely into four so that
 * we can display a grid with four labels that don't have crazy decimal values.
 *
 * This is accomplished by rounding to two significant figures to get close to the maxCount
 * and then finding the nearest multiple of 2 or 4. If the maxCount is less than 100, we round
 * to the nearest multiple of 4 because we don't want to result in a bar that has a decimal point
 * but if the maxCount is greater than 100, we round to the nearest multiple of 2 because showing
 * something like 125 on the grid is acceptable if the last bar is 500.
 */
function getLastBar(maxCount: number) {
  // round the last bar down to two significant figures
  // for example, 193 => 190, 256 => 250, 1340 => 1300, 5000 => 5000
  const orderOfMagnitude = Math.pow(10, Math.floor(Math.log10(maxCount)) - 1);
  const twoSignificantFigures = Math.floor(maxCount / orderOfMagnitude);
  // then round down to the nearest multiple of 2 or 4 if maxCount < 100
  // for example, 193 => 180, 256 => 240, 1340 => 1200, 5000 => 5000
  return (
    (twoSignificantFigures - (twoSignificantFigures % (maxCount < 100 ? 4 : 2))) * orderOfMagnitude
  );
}

export function ApplicationsPipeline({ steps }: { steps: { name: string; count: number }[] }) {
  const maxCount = steps.reduce((max, step) => Math.max(max, step.count), 20);
  const lastBar = getLastBar(maxCount);
  return (
    <ContentCard kind="bordered">
      <Row>
        <Row.Item>
          <h3>Applications pipeline</h3>
        </Row.Item>
        <Row.Item shrink>
          <Button label="View applications" kind="plain" as="a" href="/applications" />
        </Row.Item>
      </Row>
      <div className={styles.PipelineContainer}>
        <div
          className={styles.PipelineGridContainer}
          style={{ width: `${(lastBar / maxCount) * 100}%` }}
        >
          <div className={styles.PipelineGridLabels}>
            {[0, lastBar / 4, lastBar / 2, (3 * lastBar) / 4, lastBar].map((count) => (
              <div
                key={count}
                className={styles.PipelineGridLabel}
                style={{ height: `${steps.length * 96}px` }}
              >
                {count >= 1000000
                  ? `${count / 1000000}M`
                  : count >= 1000
                    ? `${count / 1000}k`
                    : count}
              </div>
            ))}
          </div>
          <div className={styles.PipelineGridBars}>
            {[0, lastBar / 4, lastBar / 2, (3 * lastBar) / 4, lastBar].map((i) => (
              <div key={i} className={styles.PipelineGridBar}></div>
            ))}
          </div>
        </div>
        {steps.map((step, index) => (
          <React.Fragment key={step.name}>
            <div className={styles.PipelineStepLabelName}>{step.name}</div>
            <div className="padding--x--m margin--left--s fontColor--secondary fontWeight--semibold fontSize--s">
              {index > 0 && steps[index - 1].count > 0 && (
                <Row gapSize="xs" alignItems="center">
                  <i className="narmi-icon-arrow-down"></i>
                  <Row.Item>
                    {((step.count / steps[index - 1].count) * 100).toFixed(0)}% conversion
                  </Row.Item>
                </Row>
              )}
            </div>
            <div className={styles.PipelineStepLabelCount}>
              {numberFormatter.format(step.count)}
            </div>
            <div className={styles.PipelineStepDataBar}>
              <div
                className={styles.PipelineStepDataBarFill}
                style={{
                  width: `${(step.count / maxCount) * 100}%`,
                }}
              ></div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </ContentCard>
  );
}
